import { LinearProgress } from "@mui/material";
import { useContext } from "react";

import { AuthContext } from "~/auth/AuthProvider";
import Forbidden from "~/pages/Forbidden";

interface RequireAuthProps {
	/**
	 * Authorize the user *in addition* to authentication. Defaults to a pass-through — inventory
	 * access is gated on an authenticated BPF user only (the server enforces `[Authorize]` + a BPF
	 * tenant guard), so no route supplies a role predicate. Supply one only for a genuinely
	 * role-restricted route.
	 * @default () => true
	 */
	authorize?: (user?: Maybe<SFUser>) => boolean;
	children: React.ReactNode;
	/**
	 * What to do when the user is not authorized:
	 * - `skip`: render nothing (e.g. a nav item the user may not access)
	 * - `forbid`: render the Forbidden (403) page
	 * - `redirect`: send the user to login
	 * @default "skip"
	 */
	unauthorizedBehavior?: AuthGateBehavior;
}

/**
 * Gates rendering on an authenticated session plus an optional `authorize` predicate. Renders a
 * progress bar until the initial session check resolves — never flashes content or redirects
 * before `sessionChecked` is true.
 */
function RequireAuth({ authorize = () => true, children, unauthorizedBehavior = "skip" }: RequireAuthProps) {
	const { currentUser, loggedIn, redirectToLogin, sessionChecked } = useContext(AuthContext);

	if (!sessionChecked) {
		return <LinearProgress />;
	}

	if (loggedIn && authorize(currentUser)) {
		return <>{children}</>;
	}

	switch (unauthorizedBehavior) {
		case "forbid":
			return <Forbidden />;
		case "redirect":
			redirectToLogin();
			return <></>;
		default:
			return <></>;
	}
}

export default RequireAuth;
